// FILE: src/admin/pages/support/SupportEntityIndex.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { searchSupportUsers, searchSupportOwners, searchSupportDrivers } from "../../../api/adminSupportApi";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

const ENTITY_LABEL = { users: "Users", owners: "Owners", drivers: "Drivers" };
const PAGE_SIZES = [10, 20, 50, 100];

/* --------------------- icons --------------------- */
function IconSearch({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M10.5 18.5a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z" stroke="currentColor" strokeWidth="2" />
      <path d="M16.5 16.5 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconArrowRight({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M10 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconChevronDown({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* --------------------- small utils --------------------- */
function safeStr(x) {
  return x == null ? "" : String(x).trim();
}
function numOr0(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}
function boolish(v) {
  if (typeof v === "boolean") return v;
  const s = String(v ?? "").toLowerCase().trim();
  if (!s) return false;
  return s === "true" || s === "1" || s === "yes" || s === "y";
}

// Backend uses phone for Users/Drivers, phoneNumber for Owners (sometimes).
function getPhone(row) {
  const a = safeStr(row?.phoneNumber);
  if (a) return a;
  const b = safeStr(row?.phone);
  if (b) return b;
  return "—";
}

function normalizeEntityType(raw) {
  const x = String(raw || "").trim();
  if (x === "owners" || x === "drivers" || x === "users") return x;
  return "users";
}

/* --------------------- badges --------------------- */
// USERS only
function StatusBadge({ status }) {
  const s = String(status || "").toUpperCase();
  const cfg =
    s === "ACTIVE"
      ? { label: "ACTIVE", dot: "#16A34A", cls: "border-emerald-200 bg-emerald-50 text-emerald-800" }
      : s === "BLOCKED"
      ? { label: "BLOCKED", dot: "#EF4444", cls: "border-rose-200 bg-rose-50 text-rose-800" }
      : { label: s || "UNKNOWN", dot: "#64748B", cls: "border-slate-200 bg-slate-50 text-slate-700" };

  return (
    <span
      className={classNames("inline-flex items-center gap-2 border px-3 py-1.5 text-[11px] tracking-wide whitespace-nowrap", cfg.cls)}
      style={{ borderRadius: 10 }}
      title={cfg.label}
    >
      <span className="h-2 w-2" style={{ background: cfg.dot, borderRadius: 3 }} aria-hidden="true" />
      {cfg.label}
    </span>
  );
}

function Chip({ label, tone = "slate", title }) {
  const cfg =
    tone === "green"
      ? { dot: "#16A34A", cls: "border-emerald-200 bg-emerald-50 text-emerald-800" }
      : tone === "rose"
      ? { dot: "#EF4444", cls: "border-rose-200 bg-rose-50 text-rose-800" }
      : tone === "amber"
      ? { dot: "#F59E0B", cls: "border-amber-200 bg-amber-50 text-amber-900" }
      : { dot: "#64748B", cls: "border-slate-200 bg-slate-50 text-slate-700" };

  return (
    <span
      className={classNames("inline-flex items-center gap-2 border px-3 py-1.5 text-[11px] tracking-wide whitespace-nowrap", cfg.cls)}
      style={{ borderRadius: 10 }}
      title={title || label}
    >
      <span className="h-2 w-2" style={{ background: cfg.dot, borderRadius: 3 }} aria-hidden="true" />
      {label}
    </span>
  );
}

function DocsChip({ row }) {
  const complete = boolish(row?.documentsComplete ?? row?.docsComplete ?? row?.isDocumentsComplete);
  const verified = boolish(row?.documentsVerified ?? row?.docsVerified ?? row?.isDocumentsVerified);

  if (verified) return <Chip label="VERIFIED" tone="green" title="Documents verified" />;
  if (complete) return <Chip label="COMPLETE" tone="amber" title="Documents complete (pending verify)" />;
  return <Chip label="INCOMPLETE" tone="rose" title="Documents incomplete" />;
}

/* --------------------- table helpers --------------------- */
function SkeletonRow({ cols = 6 }) {
  return (
    <tr className="border-b border-slate-200">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-5">
          <div className="h-3 w-full max-w-[240px] animate-pulse rounded bg-slate-100" />
        </td>
      ))}
    </tr>
  );
}

function clampPage(p, totalPages) {
  if (!Number.isFinite(totalPages) || totalPages <= 0) return 0;
  return Math.max(0, Math.min(p, totalPages - 1));
}

function buildPages(current, total) {
  if (!Number.isFinite(total) || total <= 1) return [0];
  const out = [];
  const last = total - 1;

  const push = (x) => out.push(x);
  const addRange = (a, b) => {
    for (let i = a; i <= b; i++) push(i);
  };

  push(0);
  const left = Math.max(1, current - 1);
  const right = Math.min(last - 1, current + 1);

  if (left > 1) push("…");
  addRange(left, right);
  if (right < last - 1) push("…");

  if (last !== 0) push(last);

  return out.filter((v, i) => i === 0 || v !== out[i - 1]);
}

/* --------------------- main --------------------- */
export default function SupportEntityIndex() {
  const { entityType: rawEntityType } = useParams();
  const entityType = normalizeEntityType(rawEntityType);

  const label = ENTITY_LABEL[entityType] || "Users";
  const enabled = entityType === "users" || entityType === "owners" || entityType === "drivers";
  const isOwners = entityType === "owners";
  const isDrivers = entityType === "drivers";

  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const debounceRef = useRef(null);
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    setPage(0);
  }, [q, size, entityType]);

  useEffect(() => {
    if (!enabled) return;

    setErr("");
    setLoading(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const mySeq = ++fetchSeqRef.current;

    debounceRef.current = setTimeout(async () => {
      try {
        let res;
        if (entityType === "owners") res = await searchSupportOwners(q, page, size);
        else if (entityType === "drivers") res = await searchSupportDrivers(q, page, size);
        else res = await searchSupportUsers(q, page, size);

        if (mySeq !== fetchSeqRef.current) return;

        const content = Array.isArray(res?.content) ? res.content : [];
        const te = Number(res?.totalElements ?? content.length ?? 0);
        const tp = Number(res?.totalPages ?? (te ? Math.ceil(te / size) : 0));

        setRows(content);
        setTotal(te);
        setTotalPages(tp);

        const safe = clampPage(page, tp);
        if (safe !== page) setPage(safe);
      } catch (e) {
        if (mySeq !== fetchSeqRef.current) return;
        setErr(e?.message || "Failed to load results");
        setRows([]);
        setTotal(0);
        setTotalPages(0);
      } finally {
        if (mySeq === fetchSeqRef.current) setLoading(false);
      }
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q, page, size, enabled, entityType]);

  const pageLabel = useMemo(() => {
    if (!total) return "0";
    const from = page * size + 1;
    const to = Math.min(total, (page + 1) * size);
    return `${from}–${to}`;
  }, [page, size, total]);

  const pages = useMemo(() => buildPages(page, totalPages), [page, totalPages]);

  // ✅ Updated col counts:
  // owners => 8 cols (unchanged)
  // drivers => 7 cols (removed Online/Tracked/Last activity, added Booking count + Ride count)
  // users => 6 cols (unchanged)
  const desktopCols = isOwners ? 8 : isDrivers ? 7 : 6;

  const colgroup = useMemo(() => {
    if (isOwners) {
      return (
        <colgroup>
          <col style={{ width: "72px" }} />  {/* ID */}
          <col style={{ width: "220px" }} /> {/* Owner */}
          <col style={{ width: "92px" }} />  {/* Vehicles */}
          <col style={{ width: "92px" }} />  {/* Bookings */}
          <col style={{ width: "92px" }} />  {/* Open offers */}
          <col style={{ width: "110px" }} /> {/* Pending payouts */}
          <col style={{ width: "140px" }} /> {/* Phone */}
          <col style={{ width: "140px" }} /> {/* Action */}
        </colgroup>
      );
    }

    if (isDrivers) {
      return (
        <colgroup>
          <col style={{ width: "90px" }} />  {/* ID */}
          <col style={{ width: "260px" }} /> {/* Driver */}
          <col style={{ width: "160px" }} /> {/* Phone */}
          <col style={{ width: "140px" }} /> {/* Docs */}
          <col style={{ width: "140px" }} /> {/* Booking count */}
          <col style={{ width: "120px" }} /> {/* Ride count */}
          <col style={{ width: "150px" }} /> {/* Action */}
        </colgroup>
      );
    }

    return (
      <colgroup>
        <col style={{ width: "80px" }} />  {/* ID */}
        <col style={{ width: "260px" }} /> {/* User name */}
        <col style={{ width: "160px" }} /> {/* Phone */}
        <col style={{ width: "140px" }} /> {/* Status */}
        <col style={{ width: "180px" }} /> {/* Last activity */}
        <col style={{ width: "150px" }} /> {/* Action */}
      </colgroup>
    );
  }, [isOwners, isDrivers]);

  const searchPlaceholder = useMemo(() => {
    if (!enabled) return `${label} search will be enabled next`;
    if (isOwners) return "Search Owners by id / name / phone";
    if (isDrivers) return "Search Drivers by id / name / phone";
    return `Search ${label} by id / name / phone`;
  }, [enabled, label, isOwners, isDrivers]);

  // ✅ Updated min width for drivers after column change
  const tableMinWidth = isOwners ? "min-w-[1050px]" : isDrivers ? "min-w-[1050px]" : "min-w-[900px]";

  return (
    <div className="min-w-0">
      {/* Search */}
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <IconSearch className="h-4 w-4 text-slate-500 shrink-0" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            disabled={!enabled}
            className="w-full bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400 disabled:opacity-60"
          />
        </div>
      </div>

      {/* Card */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-[12px] font-semibold uppercase tracking-wider text-slate-500">Results</div>
            <div className="mt-0.5 text-[12px] font-semibold text-slate-500">
              {loading ? "Loading…" : total ? `Showing ${pageLabel} of ${total}` : "No records"}
            </div>
          </div>

          {err ? <div className="text-[12px] font-semibold text-rose-600">{err}</div> : null}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block">
          <div className="w-full overflow-x-auto">
            <table className={classNames(tableMinWidth, "w-full table-fixed")}>
              {colgroup}

              <thead className="bg-slate-50 sticky top-0 z-10">
                {isOwners ? (
                  <tr className="border-b border-slate-200">
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">ID</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Owner</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">Vehicles</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">Bookings</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">Open offers</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">Pending payouts</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Phone</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">Action</th>
                  </tr>
                ) : isDrivers ? (
                  <tr className="border-b border-slate-200">
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">ID</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Driver</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Phone</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Docs</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">Bookings</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">Rides</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">Action</th>
                  </tr>
                ) : (
                  <tr className="border-b border-slate-200">
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">ID</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">User name</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Phone</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Status</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Last activity</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">Action</th>
                  </tr>
                )}
              </thead>

              <tbody className="divide-y divide-slate-200">
                {loading ? (
                  <>
                    <SkeletonRow cols={desktopCols} />
                    <SkeletonRow cols={desktopCols} />
                    <SkeletonRow cols={desktopCols} />
                  </>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={desktopCols} className="px-6 py-16 text-center">
                      <div className="text-sm font-semibold text-slate-900">No results found</div>
                      <div className="mt-1 text-[12px] text-slate-500">Try searching by phone or ID.</div>
                    </td>
                  </tr>
                ) : isOwners ? (
                  // owners unchanged
                  rows.map((r, idx) => (
                    <tr
                      key={r.id ?? idx}
                      className={classNames("hover:bg-slate-50", idx % 2 === 0 ? "bg-white" : "bg-slate-50/30")}
                    >
                      <td className="px-4 py-4 align-middle">
                        <div className="text-[13px] text-slate-900 tabular-nums whitespace-nowrap">{r.id ?? "—"}</div>
                      </td>

                      <td className="px-4 py-4 align-middle">
                        <div className="min-w-0">
                          <div className="text-[13px] text-slate-900 truncate">{r.name || "—"}</div>
                          {r.email ? (
                            <div className="mt-0.5 text-[12px] text-slate-500 truncate">{r.email}</div>
                          ) : (
                            <div className="mt-0.5 text-[12px] text-slate-400 truncate">—</div>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-4 align-middle text-right">
                        <div className="text-[13px] text-slate-900 tabular-nums whitespace-nowrap">{numOr0(r.vehicleCount)}</div>
                      </td>

                      <td className="px-4 py-4 align-middle text-right">
                        <div className="text-[13px] text-slate-900 tabular-nums whitespace-nowrap">{numOr0(r.bookingCount)}</div>
                      </td>

                      <td className="px-4 py-4 align-middle text-right">
                        <div className="text-[13px] text-slate-900 tabular-nums whitespace-nowrap">{numOr0(r.pendingOfferCount)}</div>
                      </td>

                      <td className="px-4 py-4 align-middle text-right">
                        <div className="text-[13px] text-slate-900 tabular-nums whitespace-nowrap">{numOr0(r.pendingPayoutCount)}</div>
                      </td>

                      <td className="px-4 py-4 align-middle">
                        <div className="text-[13px] text-slate-900 tabular-nums whitespace-nowrap">{getPhone(r)}</div>
                      </td>

                      <td className="px-4 py-4 align-middle text-right">
                        <Link
                          to={`/admin/support/${entityType}/${r.id}/overview`}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50 whitespace-nowrap"
                        >
                          Open
                          <IconArrowRight className="h-4 w-4 text-slate-500" />
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : isDrivers ? (
                  // ✅ drivers adjusted
                  rows.map((r, idx) => (
                    <tr
                      key={r.id ?? idx}
                      className={classNames("hover:bg-slate-50", idx % 2 === 0 ? "bg-white" : "bg-slate-50/30")}
                    >
                      <td className="px-4 py-4 align-middle">
                        <div className="text-[13px] text-slate-900 tabular-nums whitespace-nowrap">{r.id ?? "—"}</div>
                      </td>

                      {/* ✅ Driver cell: ONLY name (no email line) */}
                      <td className="px-4 py-4 align-middle">
                        <div className="text-[13px] text-slate-900 truncate">{r.name || "—"}</div>
                      </td>

                      <td className="px-4 py-4 align-middle">
                        <div className="text-[13px] text-slate-900 tabular-nums whitespace-nowrap">{getPhone(r)}</div>
                      </td>

                      <td className="px-4 py-4 align-middle">
                        <DocsChip row={r} />
                      </td>

                      {/* ✅ Booking count */}
                      <td className="px-4 py-4 align-middle text-right">
                        <div className="text-[13px] text-slate-900 tabular-nums whitespace-nowrap">
                          {numOr0(r.bookingCount)}
                        </div>
                      </td>

                      {/* ✅ Ride count */}
                      <td className="px-4 py-4 align-middle text-right">
                        <div className="text-[13px] text-slate-900 tabular-nums whitespace-nowrap">
                          {numOr0(r.rideCount)}
                        </div>
                      </td>

                      <td className="px-4 py-4 align-middle text-right">
                        <Link
                          to={`/admin/support/${entityType}/${r.id}/overview`}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50 whitespace-nowrap"
                        >
                          Open
                          <IconArrowRight className="h-4 w-4 text-slate-500" />
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  // users unchanged
                  rows.map((r, idx) => (
                    <tr
                      key={r.id ?? idx}
                      className={classNames("hover:bg-slate-50", idx % 2 === 0 ? "bg-white" : "bg-slate-50/30")}
                    >
                      <td className="px-4 py-4 align-middle">
                        <div className="text-[13px] text-slate-900 tabular-nums whitespace-nowrap">{r.id ?? "—"}</div>
                      </td>

                      <td className="px-4 py-4 align-middle">
                        <div className="text-[13px] text-slate-900 truncate">{r.name || "—"}</div>
                        {safeStr(r.email) ? <div className="mt-0.5 text-[12px] text-slate-500 truncate">{r.email}</div> : null}
                      </td>

                      <td className="px-4 py-4 align-middle">
                        <div className="text-[13px] text-slate-900 tabular-nums whitespace-nowrap">{getPhone(r)}</div>
                      </td>

                      <td className="px-4 py-4 align-middle">
                        <StatusBadge status={r.status} />
                      </td>

                      <td className="px-4 py-4 align-middle">
                        <div className="text-[12px] text-slate-700 whitespace-nowrap">{safeStr(r.lastActivityAt) || "—"}</div>
                      </td>

                      <td className="px-4 py-4 align-middle text-right">
                        <Link
                          to={`/admin/support/${entityType}/${r.id}/overview`}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50 whitespace-nowrap"
                        >
                          Open
                          <IconArrowRight className="h-4 w-4 text-slate-500" />
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ✅ Mobile cards */}
        <div className="md:hidden divide-y divide-slate-200">
          {loading ? (
            <div className="px-6 py-6 text-sm font-semibold text-slate-600">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="text-sm font-semibold text-slate-900">No results found</div>
              <div className="mt-1 text-[12px] text-slate-500">Try searching by phone or ID.</div>
            </div>
          ) : isOwners ? (
            // owners unchanged
            rows.map((r) => (
              <div key={r.id} className="px-6 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[12px] font-bold text-slate-500">
                      ID <span className="text-slate-900 tabular-nums">{r.id ?? "—"}</span>
                    </div>

                    <div className="mt-1 truncate text-sm font-semibold text-slate-900">{r.name || "—"}</div>
                    <div className="mt-2 text-[13px] font-semibold text-slate-900 tabular-nums">{getPhone(r)}</div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Vehicles</div>
                        <div className="mt-1 text-[13px] font-extrabold text-slate-900 tabular-nums">{numOr0(r.vehicleCount)}</div>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Bookings</div>
                        <div className="mt-1 text-[13px] font-extrabold text-slate-900 tabular-nums">{numOr0(r.bookingCount)}</div>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Offers</div>
                        <div className="mt-1 text-[13px] font-extrabold text-slate-900 tabular-nums">{numOr0(r.pendingOfferCount)}</div>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Payouts</div>
                        <div className="mt-1 text-[13px] font-extrabold text-slate-900 tabular-nums">{numOr0(r.pendingPayoutCount)}</div>
                      </div>
                    </div>
                  </div>

                  <Link
                    to={`/admin/support/${entityType}/${r.id}/overview`}
                    className="mt-1 inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                  >
                    Open
                    <IconArrowRight className="h-4 w-4 text-slate-500" />
                  </Link>
                </div>
              </div>
            ))
          ) : isDrivers ? (
            // ✅ drivers adjusted mobile (no email, no online/tracked/last activity; show booking+ride counts)
            rows.map((r) => (
              <div key={r.id} className="px-6 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[12px] font-bold text-slate-500">
                      ID <span className="text-slate-900 tabular-nums">{r.id ?? "—"}</span>
                    </div>

                    <div className="mt-1 truncate text-sm font-semibold text-slate-900">{r.name || "—"}</div>
                    <div className="mt-2 text-[13px] font-semibold text-slate-900 tabular-nums">{getPhone(r)}</div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <DocsChip row={r} />
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Bookings</div>
                        <div className="mt-1 text-[13px] font-extrabold text-slate-900 tabular-nums">{numOr0(r.bookingCount)}</div>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rides</div>
                        <div className="mt-1 text-[13px] font-extrabold text-slate-900 tabular-nums">{numOr0(r.rideCount)}</div>
                      </div>
                    </div>
                  </div>

                  <Link
                    to={`/admin/support/${entityType}/${r.id}/overview`}
                    className="mt-1 inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                  >
                    Open
                    <IconArrowRight className="h-4 w-4 text-slate-500" />
                  </Link>
                </div>
              </div>
            ))
          ) : (
            // users unchanged
            rows.map((r) => (
              <div key={r.id} className="px-6 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[12px] font-bold text-slate-500">
                      ID <span className="text-slate-900 tabular-nums">{r.id ?? "—"}</span>
                    </div>
                    <div className="mt-1 truncate text-sm font-semibold text-slate-900">{r.name || "—"}</div>
                    <div className="mt-2 text-[13px] font-semibold text-slate-900 tabular-nums">{getPhone(r)}</div>
                    <div className="mt-2">
                      <StatusBadge status={r.status} />
                    </div>
                    <div className="mt-3 text-[12px] font-semibold text-slate-600">
                      Last activity: <span className="ml-1 font-semibold text-slate-900">{safeStr(r.lastActivityAt) || "—"}</span>
                    </div>
                  </div>

                  <Link
                    to={`/admin/support/${entityType}/${r.id}/overview`}
                    className="mt-1 inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                  >
                    Open
                    <IconArrowRight className="h-4 w-4 text-slate-500" />
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer pagination */}
        {enabled ? (
          <div className="px-5 py-3 border-t border-slate-200 bg-white">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-xs text-slate-500">
                {loading ? (
                  "Loading…"
                ) : total ? (
                  <>
                    Showing <span className="font-semibold text-slate-700">{pageLabel}</span> of{" "}
                    <span className="font-semibold text-slate-700">{total}</span>
                  </>
                ) : (
                  "No records"
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="text-xs font-semibold text-slate-500">View</div>
                  <div className="relative">
                    <select
                      value={size}
                      onChange={(e) => setSize(Number(e.target.value))}
                      className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50 outline-none"
                    >
                      {PAGE_SIZES.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    <IconChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  </div>
                </div>

                <div className="text-sm text-slate-700">
                  Page <span className="font-semibold">{Math.min(totalPages, page + 1)}</span> of{" "}
                  <span className="font-semibold">{Math.max(totalPages, 1)}</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={loading || page <= 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={loading || page >= totalPages - 1}
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            {totalPages > 1 ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {pages.map((p, i) =>
                  p === "…" ? (
                    <span key={`e-${i}`} className="px-2 text-sm text-slate-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      disabled={loading}
                      onClick={() => setPage(Number(p))}
                      className={classNames(
                        "h-9 min-w-[36px] px-3 rounded-md border text-sm font-semibold",
                        p === page
                          ? "border-slate-300 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                        loading ? "opacity-60" : ""
                      )}
                    >
                      {Number(p) + 1}
                    </button>
                  )
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {!enabled ? (
        <div className="mt-3 text-[12px] font-semibold text-slate-500">{label} support search will be enabled next.</div>
      ) : null}
    </div>
  );
}
